
    import { Component, Vue } from 'vue-property-decorator';
    import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
    import moment from 'moment';
    import { SweetAlertResult } from 'sweetalert2';
    import { confirmSwal } from '@/helper/swals';
    import Karteieintrag from '@/types/Karteieintrag';
    import { PatientStatus } from '@/types/PatientStatus';
    import User from '@/types/User';
    import {
        ElgaUebertagungFilter,
        ElgauebertragungEintrag,
        UebertragungStatus,
    } from '@/store/types';
    import {
        PruefenImpfungenPayload,
        StornierenImpfungenPayload,
        SuchenImpfungenPayload,
        UebertragenImpfungenPayload,
    } from '@/store/elgauebertragung';
    import { DataTableHeaders } from '@/types';

    @Component({
        computed: {
            ...mapGetters('authentication', ['user', 'isAllowed']),
            ...mapState('elgauebertragung', ['filter', 'items', 'selectedItems', 'patienten', 'page', 'loaders']),
            ...mapGetters('elgauebertragung', ['isUebertragen', 'isStornieren']),
        },
        methods: {
            ...mapActions('patient', ['loadPatient']),
            ...mapActions('elgauebertragung', [
                'loadImpfungen',
                'uebertragenImpfungen',
                'stornierenImpfungen',
                'suchenPatienten',
                'pruefenPatienten',
                'exportImpfungen',
            ]),
            ...mapMutations('elgauebertragung', ['setPage', 'setSelectedItems']),
        },
    })
    export default class ElgaUebertagung extends Vue {
        public loadPatient!: (payload: { patnr: number }) => Promise<void>;
        public user!: User | null;

        public items!: ElgauebertragungEintrag[];
        public patienten!: ElgauebertragungEintrag[];
        public page!: number;
        public filter!: ElgaUebertagungFilter;
        public selectedItems!: ElgauebertragungEintrag[];

        public loadImpfungen!: (filter: ElgaUebertagungFilter) => void;
        public uebertragenImpfungen!: (payload: UebertragenImpfungenPayload) => void;
        public stornierenImpfungen!: (payload: StornierenImpfungenPayload) => void;
        public suchenPatienten!: (payload: SuchenImpfungenPayload) => void;
        public pruefenPatienten!: (payload: PruefenImpfungenPayload) => void;

        public setPage!: (page: number) => void;
        public setSelectedItems!: (items: ElgauebertragungEintrag[]) => void;

        public zeitraumMenu = null;

        public uebertragungStatus: { text: string; value: UebertragungStatus }[] = [
            { text: 'Nicht Übertragen', value: UebertragungStatus.NichtUebertragen },
            { text: 'Wird Übertragen', value: UebertragungStatus.WirdUebertragen },
            { text: 'Wird Storniert', value: UebertragungStatus.WirdStorniert },
            { text: 'Übertragen', value: UebertragungStatus.Uebertragen },
        ];

        public patientStatus: { text: string; value: PatientStatus, divider: boolean }[] = [
            { text: 'Zuordnung', value: PatientStatus.Zuordnung, divider: false },
            { text: 'Keine Zuordnung', value: PatientStatus.KeineZuordnung, divider: false },
            { text: 'Gefunden', value: PatientStatus.Gefunden, divider: true },
            { text: 'Nicht Gefunden', value: PatientStatus.NichtGefunden, divider: false },
            { text: 'Nicht Gesucht', value: PatientStatus.NichtGesucht, divider: false },
            { text: 'Unklare Übereinstimmung', value: PatientStatus.Unklar, divider: false },
        ];

        public get headers(): DataTableHeaders<ElgauebertragungEintrag, 'patient' | 'impfungen' | 'actions'> {
            return [
                { text: 'Patient', value: 'patient', sortable: false },
                { text: 'Impfungen', value: 'impfungen', sortable: false },
                { text: '', value: 'actions', sortable: false, width: 50 },
            ];
        }

        public get karteiHeaders(): DataTableHeaders<ElgauebertragungEintrag> {
            return [
                { text: 'Datum', value: 'impfdatumDisplay', sortable: false, width: 110 },
                { text: 'Impfung', value: 'textDisplay', sortable: false },
                { text: 'Arzt', value: 'aerzte_arzt', sortable: false, width: 110 },
            ];
        }

        public get releaseDate(): string | undefined {
            return this.user?.release_date ?? undefined;
        }

        public get canUebertragen(): boolean {
            return this.selectedItems.filter((item: Karteieintrag) => item.eimpf_id === null).length !== 0;
        }

        public get canStornieren(): boolean {
            return this.selectedItems.filter((item: Karteieintrag) => item.eimpf_id !== null).length !== 0;
        }

        public get canSuchen(): boolean {
            return this.selectedItems.length !== 0;
        }

        public get canExport(): boolean {
            return this.items.length !== 0;
        }

        public get currentPage(): number {
            return this.page;
        }

        public set currentPage(page: number) {
            this.setPage(page);
        }

        public get selectedPatienten(): ElgauebertragungEintrag[] {
            return this.selectedItems;
        }

        public set selectedPatienten(items: ElgauebertragungEintrag[]) {
            this.setSelectedItems(items);
        }

        public get maxZeitraum(): string {
            return moment().format('YYYY-MM-DD');
        }

        public get zeitraumLabel(): string {
            return this.filter.zeitraum.length
                ? this.filter.zeitraum.sort((a: string, b: string) => a.localeCompare(b)).map((date: string) => {
                    const [year, month, day] = date.split('-');
                    return `${day}.${month}.${year}`;
                }).join(' - ')
                : '';
        }

        public mounted(): void {
            this.onClickFiltern();
        }

        public reloadImpfungen(): void {
            //this.setSelectedItems([]);
            this.loadImpfungen(this.filter);
        }

        public patientName(item: ElgauebertragungEintrag): string {
            return [
                item.patient_nachname,
                item.patient_vorname,
                item.patient_titel,
                item.patient_titel_nach_name,
                item.patient_geburtsdatum ? `geb. ${this.$d(item.patient_geburtsdatum)}` : null,
            ].filter((str) => str).join(', ');
        }

        public impfungen({ patnr }: ElgauebertragungEintrag): ElgauebertragungEintrag[] {
            return this.items.filter((item) => item.patnr === patnr);
        }

        public onClickFiltern(): void {
            this.reloadImpfungen();
            this.setPage(1);
        }

        public onClickPatient(eintrag: Karteieintrag): void {
            this.$router.push('/');
            this.loadPatient({ patnr: eintrag.patnr as number });
        }

        public onClickUebertragen(): void {
            const patienten = this.selectedItems.map((item: Karteieintrag) => item.patnr as number);
            const impfungen = this.items.filter((item: Karteieintrag) => patienten.includes(item.patnr as number));

            const werden = this.items.length === 1 ? 'wird' : 'werden';
            const en = (array: Array<unknown>): string => array.length === 1 ? '' : 'en';

            confirmSwal(
                'Übertragung',
                `Es ${werden} ${impfungen.length} Impfung${en(impfungen)}` +
                    ` von ${patienten.length} Patient${en(patienten)} an die ELGA` +
                    ' übertragen. Sind Sie sicher?',
                'Übertragen',
                'Abbrechen',
            ).then(({ isConfirmed }: SweetAlertResult) => {
                if (isConfirmed) {
                    this.uebertragenImpfungen({ patienten });
                }
            });
        }

        public onClickStornieren(): void {
            const patienten = this.selectedItems.map((item: Karteieintrag) => item.patnr as number);
            const impfungen = this.items.filter((item: Karteieintrag) => patienten.includes(item.patnr as number));

            const werden = this.items.length === 1 ? 'wird' : 'werden';
            const en = (array: Array<unknown>): string => array.length === 1 ? '' : 'en';

            confirmSwal(
                'Stornierung',
                `Es ${werden} ${impfungen.length} Impfung${en(impfungen)}` +
                    ` von ${patienten.length} Patient${en(patienten)} ` +
                    ' storniert. Sind Sie sicher?',
                'Stornieren',
                'Abbrechen',
            ).then(({ isConfirmed }: SweetAlertResult) => {
                if (isConfirmed) {
                    this.stornierenImpfungen({ patienten });
                }
            });
        }

        public onClickSearch(): void {
            const patienten = this.selectedItems.map((item: Karteieintrag) => item.patnr as number);

            const werden = patienten.length === 1 ? 'wird' : 'werden';
            const en = (array: Array<unknown>): string => array.length === 1 ? '' : 'en';

            confirmSwal(
                'Suche',
                `Es ${werden} ${patienten.length} Patient${en(patienten)} ` +
                    ' gesucht. Sind Sie sicher?',
                'Suchen',
                'Abbrechen',
            ).then(({ isConfirmed }: SweetAlertResult) => {
                if (isConfirmed) {
                    this.suchenPatienten({ patienten });
                }
            });
        }

        public onClickVerify(): void {
            const patienten = this.selectedItems.map((item: Karteieintrag) => item.patnr as number);

            const werden = patienten.length === 1 ? 'wird' : 'werden';
            const en = (array: Array<unknown>): string => array.length === 1 ? '' : 'en';

            confirmSwal(
                'Prüfung',
                `Es ${werden} ${patienten.length} Patient${en(patienten)} ` +
                    ' geprüft. Sind Sie sicher?',
                'Prüfen',
                'Abbrechen',
            ).then(({ isConfirmed }: SweetAlertResult) => {
                if (isConfirmed) {
                    this.pruefenPatienten({ patienten });
                }
            });
        }
    }
